import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useMemo,
  useCallback,
  useRef,
} from "react";

/**
 * 🌐 PUBLIC SERVICES
 */
import { publicSettingsService } from "../api/service/public/settings.service";
import { publicServicesService } from "../api/service/public/services.service";
import { publicProductsService } from "../api/service/public/products.service";
import { publicContactService } from "../api/service/public/contact.service";
import { publicArticlesService } from "../api/service/public/articles.service";

/**
 * 📦 INTERFACES
 */
import { CompanySettings } from "../api/interface/public.interface";
import { Service } from "../api/interface/services.interface";
import { Product } from "../api/interface/products.interface";
import { ProductCategory } from "../api/interface/productcategories.interface";
import { Article } from "../api/interface/articles.interface";

// ===============================
// TYPES
// ===============================
interface PublicContextType {
  settings: CompanySettings | null;

  services: Service[] | null;
  products: Product[] | null;
  articles: Article[] | null;

  productCategories: ProductCategory[] | null;

  isLoadingSettings: boolean;
  isLoadingServices: boolean;
  isLoadingProducts: boolean;
  isLoadingArticles: boolean;
  isLoadingProductCategories: boolean;

  refreshSettings: () => Promise<void>;
  refreshServices: () => Promise<void>;
  refreshProducts: () => Promise<void>;
  refreshArticles: () => Promise<void>;
  refreshProductCategories: () => Promise<void>;

  sendContactMessage: (payload: any) => Promise<any>;
}

// ===============================
// CONTEXT
// ===============================
const PublicContext = createContext<PublicContextType | undefined>(undefined);

// ===============================
// PROVIDER
// ===============================
export const PublicProvider = ({ children }: { children: ReactNode }) => {
  // ===============================
  // STATE
  // ===============================
  const [settings, setSettings] = useState<CompanySettings | null>(null);
  const [services, setServices] = useState<Service[] | null>(null);
  const [products, setProducts] = useState<Product[] | null>(null);
  const [articles, setArticles] = useState<Article[] | null>(null);
  const [productCategories, setProductCategories] = useState<ProductCategory[] | null>(null);

  // ===============================
  // LOADING STATES
  // ===============================
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [isLoadingServices, setIsLoadingServices] = useState(false);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [isLoadingArticles, setIsLoadingArticles] = useState(false);
  const [isLoadingProductCategories, setIsLoadingProductCategories] = useState(false);

  // ===============================
  // REFS (ANTI DOUBLE CALL)
  // ===============================
  const fetchingSettingsRef = useRef(false);
  const fetchingServicesRef = useRef(false);
  const fetchingProductsRef = useRef(false);
  const fetchingArticlesRef = useRef(false);
  const fetchingProductCategoriesRef = useRef(false);

  // ===============================
  // SETTINGS
  // ===============================
  const loadSettings = useCallback(async () => {
    if (fetchingSettingsRef.current) return;
    fetchingSettingsRef.current = true;

    try {
      setIsLoadingSettings(true);
      const res = await publicSettingsService.getSettings();
      setSettings(res);
    } catch {
      setSettings(null);
    } finally {
      fetchingSettingsRef.current = false;
      setIsLoadingSettings(false);
    }
  }, []);

  // ===============================
  // SERVICES
  // ===============================
  const loadServices = useCallback(async () => {
    if (fetchingServicesRef.current) return;
    fetchingServicesRef.current = true;

    try {
      setIsLoadingServices(true);
      const res = await publicServicesService.getServices();
      setServices(res.data.services);
    } catch {
      setServices(null);
    } finally {
      fetchingServicesRef.current = false;
      setIsLoadingServices(false);
    }
  }, []);

  // ===============================
  // PRODUCTS
  // ===============================
  const loadProducts = useCallback(async () => {
    if (fetchingProductsRef.current) return;
    fetchingProductsRef.current = true;

    try {
      setIsLoadingProducts(true);
      const res = await publicProductsService.getProducts();
      setProducts(res.data.products);
    } catch {
      setProducts(null);
    } finally {
      fetchingProductsRef.current = false;
      setIsLoadingProducts(false);
    }
  }, []);

  // ===============================
  // ARTICLES (✅ AJOUT)
  // ===============================
  const loadArticles = useCallback(async () => {
    if (fetchingArticlesRef.current) return;
    fetchingArticlesRef.current = true;

    try {
      setIsLoadingArticles(true);
      const res = await publicArticlesService.getArticles();
      setArticles(res.data.articles);
    } catch {
      setArticles(null);
    } finally {
      fetchingArticlesRef.current = false;
      setIsLoadingArticles(false);
    }
  }, []);

  // ===============================
  // PRODUCT CATEGORIES
  // ===============================
  const loadProductCategories = useCallback(async () => {
    if (fetchingProductCategoriesRef.current) return;
    fetchingProductCategoriesRef.current = true;

    try {
      setIsLoadingProductCategories(true);
      const res = await publicProductsService.getProductCategories();
      setProductCategories(res.data.categories);
    } catch {
      setProductCategories(null);
    } finally {
      fetchingProductCategoriesRef.current = false;
      setIsLoadingProductCategories(false);
    }
  }, []);

  // ===============================
  // CONTACT
  // ===============================
  const sendContactMessage = useCallback(async (payload: any) => {
    return await publicContactService.sendContactMessage(payload);
  }, []);

  // ===============================
  // REFRESH
  // ===============================
  const refreshSettings = useCallback(async () => loadSettings(), [loadSettings]);
  const refreshServices = useCallback(async () => loadServices(), [loadServices]);
  const refreshProducts = useCallback(async () => loadProducts(), [loadProducts]);
  const refreshArticles = useCallback(async () => loadArticles(), [loadArticles]);
  const refreshProductCategories = useCallback(async () => loadProductCategories(), [loadProductCategories]);

  // ===============================
  // INIT
  // ===============================
  useEffect(() => {
    loadSettings();
    loadServices();
    loadProducts();
    loadArticles();
    loadProductCategories();
  }, [
    loadSettings,
    loadServices,
    loadProducts,
    loadArticles,
    loadProductCategories,
  ]);

  // ===============================
  // VALUE
  // ===============================
  const value = useMemo(
    () => ({
      settings,
      services,
      products,
      articles,
      productCategories,

      isLoadingSettings,
      isLoadingServices,
      isLoadingProducts,
      isLoadingArticles,
      isLoadingProductCategories,

      refreshSettings,
      refreshServices,
      refreshProducts,
      refreshArticles,
      refreshProductCategories,

      sendContactMessage,
    }),
    [
      settings,
      services,
      products,
      articles,
      productCategories,

      isLoadingSettings,
      isLoadingServices,
      isLoadingProducts,
      isLoadingArticles,
      isLoadingProductCategories,

      refreshSettings,
      refreshServices,
      refreshProducts,
      refreshArticles,
      refreshProductCategories,

      sendContactMessage,
    ]
  );

  return (
    <PublicContext.Provider value={value}>
      {children}
    </PublicContext.Provider>
  );
};

// ===============================
// HOOK
// ===============================
export const usePublic = () => {
  const context = useContext(PublicContext);

  if (!context) {
    throw new Error("usePublic doit être utilisé dans PublicProvider");
  }

  return context;
};